import baseApi from './base.api'
import { toast } from '@/common/util'
import type { ParamsRequestVistra } from '@/types/types'
import { useAuthStore, useVistraStore, useSystemStore } from '@/stores'

export interface BasicPanelUserAccess {
  uniqueId: string
  id: string
  name: string
  public: boolean
  contractsId: string[]
  params: {
    id: string
    description: string
    type: string
    value: string
  }[]
  active: boolean
  panelType?: 'Painel' | 'Report'
}

export interface UsersFromPanel {
  id: string
  panelId: string
  userName: string
  email: string
  position: string
  unity: string
  department: string
  hasAccess: boolean
}

export const vistraService = {
  ...baseApi,
  resource: 'PainelVistraBI',

  getBasicPanelAccess(asDashboard?: boolean, tipo?: 'Painel' | 'Report') {
    return baseApi
      .get<{
        id: string
        painelId: string
        painelNome: string
        publico: boolean
        contratosIds: string[]
        parametros: {
          id: string
          description: string
          type: string
          value: string
        }[]
        ativo: boolean
        tipo: 'Painel' | 'Report'
      }[]>([vistraService.resource, !asDashboard ? '' : 'Dashboard'].join('/'), { tipo })
      .then(({ success, data }) => {
        return {
          success,
          data: (data?.map((item) => ({
            uniqueId: item.id,
            id: item.painelId,
            name: item.painelNome,
            public: item.publico,
            contractsId: item.contratosIds,
            params: item.parametros,
            active: item.ativo,
            panelType: (item.tipo === 1 || (item.tipo as any) === 'Report') ? 'Report' : 'Painel'
          })) || []) as BasicPanelUserAccess[]
        }
      })
      .catch((error) => {
        console.log('error', error)

        return {
          success: false,
          data: [] as BasicPanelUserAccess[]
        }
      })
  },
  getBasicPanelUserAccess(userId?: string, tipo?: 'Painel' | 'Report') {
    return baseApi
      .get<{
        id: string
        painelId: string
        painelNome: string
        publico: boolean
        contratosIds: string[]
        ativo: boolean
        tipo: 'Painel' | 'Report' | number
      }[]>(`${this.resource}/getBasicPanelUserAccess`, { userId, tipo })
      .then(({ success, data }) => {
        return {
          success,
          data: (data?.map((item) => ({
            uniqueId: item.id,
            id: item.painelId,
            name: item.painelNome,
            public: item.publico,
            contractsId: item.contratosIds,
            active: item.ativo,
            panelType: (item.tipo === 1 || (item.tipo as any) === 'Report') ? 'Report' : 'Painel'
          })) || []) as BasicPanelUserAccess[]
        }
      })
      .catch((error) => {
        console.log('error', error)

        return {
          success: false,
          data: [] as BasicPanelUserAccess[]
        }
      })
  },
  getUsersFromPanelId(panelId: string) {
    return baseApi
      .get<{
        id: string
        panelId: string
        name: string
        email: string
        cargo: string
        unidadeDeNegocio: string
        departamento: string
        hasAccess: boolean
      }[]>(`${this.resource}/getUsersFromPanelId`, { panelId })
      .then(({ success, data }) => {
        return {
          success,
          data: (data?.map(item => ({
            id: item.id,
            panelId: item.panelId,
            userName: item.name,
            email: item.email,
            position: item.cargo,
            unity: item.unidadeDeNegocio,
            department: item.departamento,
            hasAccess: item.hasAccess
          })) || []) as UsersFromPanel[]
        }
      })
      .catch((error) => {
        console.log('error', error)

        return {
          success: false,
          data: [] as UsersFromPanel[]
        }
      })
  },

  login() {
    return baseApi
      .post<{
        authToken?: string;
        refreshToken?: string;
        status: boolean;
      }>(`${this.resource}/Login`)
      .then(({ data }) => {
        return {
          success: true,
          data: {
            authToken: data.authToken || '',
            refreshToken: data.refreshToken || '',
            status: data.status ?? false
          }
        }
      })
      .catch((error) => {
        console.error('Login error:', error)

        const notifications = error.response?.data?.notifications as string[] || []
        toast.error({ message: notifications[0] || 'Erro ao autenticar usuário no Vistra BI' })

        return {
          success: false,
          data: {
            authToken: null,
            refreshToken: null,
            status: false
          }
        }
      })
  },

  async setParamsRequest({ panelId, params = [] }: ParamsRequestVistra) {
    params = params || []

    const biParams : { id: string, valor: any, descricao: string}[] = []

    params.forEach(el => {
      let value = el.value

      if (`${el.type}`.toLowerCase() === 'metadata') {
        switch (el.value) {
          case 'tenantId':
            value = useAuthStore().getActiveTentant
          break
          case 'superiorId':
            value = useAuthStore().persistentInfo.supervisor.id
          break
          case 'usuarioId':
            value = useAuthStore().persistentInfo.id
          break
          case 'unidadeId':
            value = useAuthStore().persistentInfo.unidade.id
          break
        }
      }

      biParams.push({
        id: el.id,
        valor: value,
        descricao: el.description
      })
    })

    return baseApi
      .post<{ status: boolean, token: string }>(`${this.resource}/SetParametrosRequisicaoExterna`, {
        painel: panelId,
        parametros: biParams,
        token: useVistraStore().vistraToken,
      })
      .then(({ data }) => {
        return {
          success: true,
          data: {
            status: data.status ?? false,
            token: data.token ?? ''
          }
        }
      })
      .catch((error) => {
        console.error('SetParamsRequest error:', error)

        const notifications = error.response?.data?.notifications as string[] || []
        toast.error({ message: notifications[0] || 'Erro ao definir parâmetros no Vistra BI' })

        return {
          success: false,
          data: {
            status: false,
            token: ''
          }
        }
      })
  },

  async setParamsRequestReport({ panelId, type = 'panel' }: ParamsRequestVistra) {
    return baseApi
      .post<{ status: boolean, token: string }>(`${this.resource}/SetParametrosRequisicaoExterna`, {
        authToken: useVistraStore().vistraToken,
        refreshToken: useVistraStore().vistraRefreshToken,
        temasEscuro: useSystemStore().isDark,
        tipo: type,
        idUnico: panelId,
        parametros: [],
        configuracoesExternasConexoesAPI: [],
        originTarget: window.location.origin,
        permitirParametrosParciais: true,
        permitirPersonalizarRelatorio: false
      })
      .then(({ data }) => {
        return {
          success: true,
          data: {
            status: data.status ?? false,
            token: data.token ?? ''
          }
        }
      })
      .catch((error) => {
        console.error('SetParamsRequest error:', error)

        const notifications = error.response?.data?.notifications as string[] || []
        toast.error({ message: notifications[0] || 'Erro ao definir parâmetros no Vistra BI' })

        return {
          success: false,
          data: {
            status: false,
            token: ''
          }
        }
      })
  }
}
